import { ImagesService } from '@/service/images/imagesService';
import type { RecipeResult } from '@/types/Recipes';

interface RecipeDetailSchemaProps {
  recipe?: RecipeResult;
  currentUrl: string;
}

const dietaryMap = {
  Diabetic: 'DiabeticDiet',
  GlutenFree: 'GlutenFreeDiet',
  Halal: 'HalalDiet',
  Hindu: 'HinduDiet',
  Kosher: 'KosherDiet',
  LowCalorie: 'LowCalorieDiet',
  LowFat: 'LowFatDiet',
  LowLactose: 'LowLactoseDiet',
  LowSalt: 'LowSaltDiet',
  Vegan: 'VeganDiet',
  Vegetarian: 'VegetarianDiet',
};

export const RecipeDetailSchema: React.FC<RecipeDetailSchemaProps> = ({ recipe, currentUrl }) => {
  if (!recipe) return null;

  const origin = URL.canParse(currentUrl) ? new URL(currentUrl).origin : '';

  const videoSchema = recipe.VideoInstruction?.Video?.YouTubeVideoLink
    ? {
        video: {
          '@type': 'VideoObject',
          name: recipe.VideoInstruction?.Video?.Title || '',
          description: recipe.VideoInstruction?.Video?.AlternativeText || '',
          thumbnailUrl: recipe.VideoInstruction?.Video?.YouTubeVideoLink
            ? `https://img.youtube.com/vi/${recipe.VideoInstruction?.Video?.YouTubeVideoLink?.split('v=')[1]}/maxresdefault.jpg`
            : '',
          contentUrl: recipe.VideoInstruction?.Video?.YouTubeVideoLink || '',
          uploadDate: `${new Date(recipe.CascadedModifiedAt).toISOString()}` || '',
          duration: '',
          interactionCount: '',
        },
      }
    : {};

  const ratingSchema =
    recipe.ratingData?.amount && recipe.ratingData.amount > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: recipe.ratingData.value?.toFixed(0),
            reviewCount: recipe.ratingData.amount,
            bestRating: 5,
          },
        }
      : {};

  const jsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'Recipe',
    '@id': currentUrl,
    mainEntityOfPage: currentUrl,
    name: recipe.Title,
    headline: recipe.Title,
    description: recipe.ShortDescription || recipe.IntroductionText || '',
    image:
      ImagesService.getAbsoluteUrl(
        ImagesService.transformUrlToProxyUrl(recipe.Images?.[0]?.Formats?.[0]?.Url || ''),
        origin,
      ) || '',
    author: {
      '@type': 'Person',
      jobTitle: recipe.Author?.JobTitle || 'Kreative Meisterbäcker:innen',
      name: recipe.Author?.Title || 'Dr. Oetker Content Kitchen',
    },
    publisher: {
      '@type': 'Organization',
      name: recipe.Organisation?.Title || 'Dr. Oetker',
      logo: {
        '@type': 'ImageObject',
        url: 'https://media.graphassets.com/qysPznNBTly3DBaldRq1',
      },
    },
    datePublished: `${new Date(recipe.PublicationDate).toISOString()}`,
    dateModified: `${new Date(recipe.CascadedModifiedAt).toISOString()}`,
    prepTime: recipe.PreparationTimeInMinutes ? `PT${recipe.PreparationTimeInMinutes}M` : '',
    cookTime: recipe.BakingTimeInMinutes ? `PT${recipe.BakingTimeInMinutes}M` : '',
    totalTime:
      recipe.BakingTimeInMinutes && recipe.PreparationTimeInMinutes
        ? `PT${recipe.BakingTimeInMinutes + recipe.PreparationTimeInMinutes}M`
        : '',
    performTime: recipe.PreparationTimeInMinutes ? `PT${recipe.PreparationTimeInMinutes}M` : '',
    recipeYield: recipe?.ServingsSummaryText,
    recipeCategory: recipe.Category?.Title || '',
    recipeCuisine: '', // Not provided in the recipe data, can be set based on additional logic if needed
    suitableForDiet: recipe.DietTypes?.map((diet: { Title: string }) => ({
      '@type': 'Diet',
      name: dietaryMap[diet.Title as keyof typeof dietaryMap] || diet.Title,
    })),
    cookingMethod: recipe.Category?.Title || '',

    tool:
      recipe.Kitchenware?.map((k: { Title: string }) => ({
        '@type': 'HowToTool',
        name: k.Title,
      })) || [],
    keywords: [
      ...recipe.Allergens,
      ...recipe.Tastes,
      ...recipe.Keywords,
      recipe.Category,
      ...recipe.DietTypes,
      ...recipe.MenuTypes,
    ]
      ?.map((keyword: { Title: string }) => keyword.Title)
      ?.join(', '),

    ...videoSchema,
    ...ratingSchema,

    recipeIngredient:
      recipe.IngredientsAndEquipmentBlocks.reduce(
        (
          acc: string[],
          curr: { Ingredients: { ArticleSummaryText: string; AmountAndUnitSummaryText: string }[] },
        ) => {
          return [
            ...acc,
            ...curr.Ingredients.map(
              (ingredient: { ArticleSummaryText: string; AmountAndUnitSummaryText: string }) =>
                `${ingredient.AmountAndUnitSummaryText ?? ''} ${ingredient.ArticleSummaryText}`.trim(),
            ),
          ];
        },
        [] as string[],
      ) || [],
    recipeInstructions:
      recipe?.ClassicInstruction?.PreparationBlocks?.map(
        (block: { Title: string; Description: string }, index: number) => ({
          '@type': 'HowToStep',
          name: block.Title || '',
          text: block.Description || '',
          url: `${currentUrl.split('?')[0].split('#')[0]}#step-${index + 1}`,
          image: '',
        }),
      )?.filter((step: { text: string }) => step.text) || [],
    nutrition: [
      {
        '@type': 'NutritionInformation',
        calories: `${recipe.NutritionalInformationPer100g.EnergyInKilocalories} kcal`,
        servingSize: recipe.ServingsSummaryText || '',
        fatContent: `${Math.round((recipe.NutritionalInformationPer100g.FatInGrams || 0) * 100) / 100} g`,
        saturatedFatContent: `${Math.round((recipe.NutritionalInformationPer100g.SaturatedFatInGrams || 0) * 100) / 100} g`,
        carbohydrateContent: `${Math.round((recipe.NutritionalInformationPer100g.CarbohydrateInGrams || 0) * 100) / 100} g`,
        sugarContent: `${Math.round((recipe.NutritionalInformationPer100g.SugarInGrams || 0) * 100) / 100} g`,
        proteinContent: `${Math.round((recipe.NutritionalInformationPer100g.ProteinInGrams || 0) * 100) / 100} g`,
        fiberContent: `${Math.round((recipe.NutritionalInformationPer100g.DietaryFiberInGrams || 0) * 100) / 100} g`,
        sodiumContent: `${Math.round((recipe.NutritionalInformationPer100g.SodiumInMilligrams || 0) * 100) / 100} mg`,
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
};
